import { StatusData } from "@fairfleet/geotab";

import InfoIcon from "@mui/icons-material/Info";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { t } from "i18next";
import { useState } from "react";
import formatDuration from "./formatDuration";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { ModalType, useModal } from "@/components/ModalProvider";
import { useFormattedUnit } from "@/hooks/metrics/useFormattedUnit";
import { useGetColorByValue } from "@/pages/diagnosticmonitor/hooks/useGetColorByValue";

interface DiagnosticCellProps {
  props: GridRenderCellParams;
}

export function DiagnosticCell({ props }: DiagnosticCellProps) {
  const { precisions, diagnosticUnits, hideOldData } = useDataGridSettings();
  const { openModal } = useModal();
  const { id, field } = props;

  const precision = field in precisions ? precisions[field] : 0;
  const [isHovered, setIsHovered] = useState(false);
  const getColorByValue = useGetColorByValue();
  // When aggregating, the bottom row has id auto-generated-group-footer-root
  const data =
    id === "auto-generated-group-footer-root"
      ? props.value
      : ((props?.row.statusData?.statusData ?? []) as StatusData[]).find(
          sd => sd.diagnostic.id === field,
        );
  const value = id === "auto-generated-group-footer-root" ? data : data?.data;
  const unit = diagnosticUnits.find(du => du.diagnosticId == field);
  const dunit = unit?.displayUnitOfMeasure;

  const formattedValue = useFormattedUnit(
    value,
    precision,
    dunit?.name,
    dunit?.factorFromBase,
    dunit?.offsetFromBase,
  );

  const now = new Date();
  const diffMillis = now.getTime() - data?.dateTime;
  const formattedTime = formatDuration(diffMillis);
  const hr24Millis = 24 * 60 * 60 * 1000;

  const valueInUnit = dunit?.factorFromBase ? value * dunit?.factorFromBase : value;
  const { bgColor, color } = getColorByValue(field, valueInUnit);

  const hideData = diffMillis > hr24Millis && hideOldData;
  return (
    <Tooltip
      sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      title={value && formattedValue ? t("deviceStatus.lastUpdated") + formattedTime : ""}
    >
      <Box
        color={hideData ? "#808080" : color}
        sx={{
          backgroundColor: !hideData ? bgColor : undefined,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!!openModal && isHovered ? (
          <IconButton onClick={() => openModal(props, ModalType.DIAGNOSTIC_DETAILS)}>
            <InfoIcon fontSize="small" />
          </IconButton>
        ) : formattedValue ? (
          formattedValue
        ) : (
          "---"
        )}
      </Box>
    </Tooltip>
  );
}
